import type { ExpressionSpecification, Map, MapMouseEvent } from "@iventis/mapbox-gl";
import { featureCollection, point } from "@turf/helpers";
import { LocationPin } from "../../types/store-schema";
import { locationPinAddHoverSpriteName, locationPinHoverSpriteName, locationPinsLayerId, locationPinsSourceId, locationPinSpriteName } from "../constants";
import { boldFontStack } from "../mapbox/engine-mapbox-types-and-constants";

interface LocationPinsOperations {
    onClick: (pinId: string) => void;
    /** Called when the mouse moves onto the pins and when it leaves them, so the cursor can show they can be clicked */
    onHoverChange: (isHovering: boolean) => void;
}

/** Mapbox layer showing pins at locations, each with its name above it */
export class MapboxLocationPinsLayer {
    /** Scale of the pin image, which is drawn at 48 pixels high */
    private readonly iconSize = 0.8;

    private readonly textSize = 13;

    /** Width in pixels a name can take before it wraps onto another line */
    private readonly textMaxWidth = 100;

    /** Gap in pixels between the pin and the name */
    private readonly textGap = 4;

    private readonly map: Map;

    private readonly operations: LocationPinsOperations;

    private hoveredPinId: string | undefined;

    constructor(map: Map, operations: LocationPinsOperations) {
        this.map = map;
        this.operations = operations;
    }

    /** Shows the given pins, replacing any previous ones. An empty list removes the layer */
    public setPins(pins: LocationPin[]) {
        if (pins.length === 0) {
            this.removeFromMap();
            return;
        }
        const source = this.map.getSource(locationPinsSourceId);
        if (source?.type === "geojson") {
            source.setData(this.toFeatureCollection(pins));
            return;
        }
        this.addToMap(pins);
    }

    private addToMap(pins: LocationPin[]) {
        this.map.addSource(locationPinsSourceId, { type: "geojson", data: this.toFeatureCollection(pins) });
        this.map.addLayer({
            id: locationPinsLayerId,
            type: "symbol",
            source: locationPinsSourceId,
            layout: {
                "icon-image": this.getIconImage(),
                "icon-size": this.iconSize,
                "icon-anchor": "bottom",
                "text-field": ["get", "name"],
                "text-font": boldFontStack,
                "text-size": this.textSize,
                // Max width is in ems
                "text-max-width": this.textMaxWidth / this.textSize,
                // The name goes where it fits, tried in this order, and is left out when it fits nowhere. The pin's point is at the bottom of its image,
                // so the name below it sits underneath the pin
                "text-variable-anchor": ["top", "right", "left"],
                // Radial offset is in ems
                "text-radial-offset": this.textGap / this.textSize,
                // The pin is always shown, even where it overlaps map objects or labels, but the name never overlaps anything
                "icon-allow-overlap": true,
                "icon-ignore-placement": false,
                "text-allow-overlap": false,
                // Without this a pin is hidden along with its name when the name doesn't fit, which is what happens when zoomed out
                "text-optional": true,
                "text-ignore-placement": false,
            },
            paint: {
                "text-color": "#000000",
                "text-halo-color": "#ffffff",
                "text-halo-width": 2,
            },
            metadata: { name: "locationPins" },
        });
        this.map.on("click", locationPinsLayerId, this.onLayerClick);
        this.map.on("mousemove", locationPinsLayerId, this.onLayerMouseMove);
        this.map.on("mouseleave", locationPinsLayerId, this.onLayerMouseLeave);
    }

    private removeFromMap() {
        this.map.off("click", locationPinsLayerId, this.onLayerClick);
        this.map.off("mousemove", locationPinsLayerId, this.onLayerMouseMove);
        this.map.off("mouseleave", locationPinsLayerId, this.onLayerMouseLeave);
        // The mouse can't leave a pin once it has been removed, so the hover is ended here
        this.setHoveredPin(undefined);
        if (this.map.getLayer(locationPinsLayerId)) {
            this.map.removeLayer(locationPinsLayerId);
        }
        if (this.map.getSource(locationPinsSourceId)) {
            this.map.removeSource(locationPinsSourceId);
        }
    }

    private onLayerClick = (event: MapMouseEvent) => {
        const pinId = event.features?.[0]?.properties?.pinId;
        if (typeof pinId === "string") {
            this.operations.onClick(pinId);
        }
    };

    private onLayerMouseMove = (event: MapMouseEvent) => {
        const pinId = event.features?.[0]?.properties?.pinId;
        this.setHoveredPin(typeof pinId === "string" ? pinId : undefined);
    };

    private onLayerMouseLeave = () => this.setHoveredPin(undefined);

    private setHoveredPin(pinId: string | undefined) {
        if (pinId === this.hoveredPinId) {
            return;
        }
        const wasHovering = this.hoveredPinId !== undefined;
        this.hoveredPinId = pinId;
        if (this.map.getLayer(locationPinsLayerId)) {
            this.map.setLayoutProperty(locationPinsLayerId, "icon-image", this.getIconImage());
        }
        if (wasHovering !== (pinId !== undefined)) {
            this.operations.onHoverChange(pinId !== undefined);
        }
    }

    /**
     * Draws the hovered pin in blue with a cross, to show clicking it removes it, or in green with a plus when clicking it adds it to a route.
     * The image can't be set from feature state, so it is chosen by pin id
     */
    private getIconImage(): ExpressionSpecification {
        const hoverImage: ExpressionSpecification = ["case", ["get", "addsToRoute"], locationPinAddHoverSpriteName, locationPinHoverSpriteName];
        return ["case", ["==", ["get", "pinId"], this.hoveredPinId ?? ""], hoverImage, locationPinSpriteName];
    }

    private toFeatureCollection(pins: LocationPin[]) {
        // The id is not stored as "id", as the engine treats a feature with a uuid "id" as a map object
        return featureCollection(pins.map((pin) => point(pin.coordinates, { pinId: pin.id, name: pin.name, addsToRoute: pin.addsToRoute ?? false })));
    }
}
